import type { PaddleEventData } from '@paddle/paddle-js';
import { promiseTimeout } from '@vueuse/core';
import { format, parse } from 'date-fns';
import { defineStore, storeToRefs } from 'pinia';
import { computed, ref, toValue, watch } from 'vue';

import { axios } from '@/helpers/axios';
import { capitalizeFirstLetter } from '@/helpers/strings';
import { useNotificationStore } from '@/modules/layout/stores/notification';
import type { Levels } from '@/modules/pricing/types';
import type { ModuleQuota, Modules, Period, Plan, PlanChanges, QuotaDetail,UserData } from '@/types/global';
import type { subscriptionPreview } from '@/types/subscription';

export interface AffiliatedData {
  affiliate: string,
  plan: Plan,
  shopName: string,
  shopId: number,
  days: number,
}

const normalizePeriod = (period: string | undefined): Period => {
  if (period && period.trim() !== '') {
    return period.toLowerCase() as Period;
  }

  return window.__DATA__?.user?.plan_id ? 'monthly' : 'annual';
};

export const useSubscriptionStore = defineStore('subscriptionStore', () => {
  const { notification } = storeToRefs(useNotificationStore());

  const planLevels = {
    free: 0,
    basic: 1,
    pro: 2,
    expert: 3,
  } satisfies Levels;

  // added '?' on `__DATA__` because it returns white screen on local error: Cannot read properties of undefined (reading 'user')
  const userDataPeriodSelected = ref<Period>(normalizePeriod(window.__DATA__?.user?.subscription_period));
  const periodSelected = ref<Period>(normalizePeriod(window.__DATA__?.user?.subscription_period));
  const upcomingPlansChanges = ref<PlanChanges | undefined>(window.__DATA__?.user?.plan_changes);
  const currentPlan = ref<Plan | null>((window.__DATA__?.user?.plan_id) ? window.__PLANS__.ids[window.__DATA__.user.plan_id] : 'free');
  const changingPlan = ref<boolean>(false);
  const selectedPlan = ref<Plan | null>(null);
  const selectedAffiliated = ref<AffiliatedData | null>(null);
  const loadingQuota = ref<boolean>(false);
  const quotas = ref<Modules>(window.__DATA__?.quota);
  const quotaNoneDaily = ref<Modules>(window.__DATA__?.quotaNoneDaily);

  const upcomingPlan = computed<Plan | null>(() => {
    if (!upcomingPlansChanges.value) return null;
    const upcomingPlanId = upcomingPlansChanges.value.scheduled_plan;

    return window.__PLANS__.ids[upcomingPlanId];
  });

  const isUpgrading = computed<boolean>(() => {
    if (!selectedPlan.value) return false;
    return getDifferenceFromCurrentPlan(selectedPlan.value) > 0;
  });

  const isPeriodUpgrading = computed<boolean>(() => (userDataPeriodSelected.value === 'monthly') && (periodSelected.value === 'annual'));

  const isCancelling = computed<boolean>(() => {
    if (!upcomingPlan.value || !upcomingPlansChanges.value) return false;
    if (upcomingPlansChanges.value.scheduled_date) {
      const scheduledDate = parse(upcomingPlansChanges.value.scheduled_date, 'yyyy-MM-dd HH:mm:ss', new Date());
      if (scheduledDate <= new Date()) return false;
    }
    return upcomingPlansChanges.value.direction === 'down' || upcomingPlansChanges.value.scheduled_plan === 0;
  });

  const currentPlanEndDate = computed<string | null>(() => {
    if (!upcomingPlansChanges.value?.scheduled_date) return null;

    const upcomingcurrentPlanEndDate = upcomingPlansChanges.value.scheduled_date;
    const parseDate = upcomingcurrentPlanEndDate ? parse(upcomingcurrentPlanEndDate, 'yyyy-MM-dd HH:mm:ss', new Date()) : null;

    const formattedDate = format(parseDate, 'MMM d, yyyy');
    return `Ends ${formattedDate}`;
  });

  const reloadQuota = async (): Promise<Modules> => {
    loadingQuota.value = true;
    const response = await axios.get<Modules>('/quota/daily');
    quotas.value = response.data;
    loadingQuota.value = false;
    return response.data;
  };

  const reloadQuotaNoneDaily = async () => {
    loadingQuota.value = true;
    const response = await axios.get<Modules>('/quota/none-daily');
    quotaNoneDaily.value = response.data;
    loadingQuota.value = false;
  };

  const getDifferenceFromCurrentPlan = (plan: Plan): number => planLevels[plan] - planLevels[currentPlan.value];

  type SubscriptionPreviewResponse = subscriptionPreview | "past_due" | "error";

  const previewPlan = async (plan: Plan): Promise<SubscriptionPreviewResponse> => {
    const planProductPriceId = window.__PLANS__.product_prices[periodSelected.value][plan];
    try {
      const response = await axios.get<subscriptionPreview>('/subscription/change-preview', {
        params: {
          price_id: planProductPriceId,
        },
      });

      return response.data;
    } catch (error) {
      if(error.response.data.includes("past_due")) {
        return "past_due";
      } else {
        return "error";
      }
    }


  };

  const changePlan = async (plan: Plan) => {
    let failedRequest = false;
    const planProductPriceId = window.__PLANS__.product_prices[periodSelected.value][plan];
    const planDifference = planLevels[plan] - planLevels[currentPlan.value];
    const isSamePeriod = (periodSelected.value === userDataPeriodSelected.value);
    const isSamePlan = (planDifference === 0);

    if (isSamePeriod && isSamePlan) return;

    if (plan === 'free') {
      const response = await axios.post('/subscription/cancel-paid');
      upcomingPlansChanges.value = response.data;
    } else {
      let direction;

      if (isSamePlan && !isSamePeriod) {
        direction = 'shift';
      } else {
        direction = planDifference < 0 ? 'down' : 'up';
      }

      await axios.post<PlanChanges>('/subscription/change-plan', {
        plan_change_direction: direction,
        price_id: planProductPriceId,
        subscription_period: capitalizeFirstLetter(periodSelected.value),
        affiliate: selectedAffiliated.value?.affiliate,
      }).catch((error) => {
        failedRequest = true;
        notification.value = {
          type: 'error',
          message: error.response.data,
          title: 'Error',
          life: 3000,
        };
      });

      if(!failedRequest) {
        currentPlan.value = plan;
        userDataPeriodSelected.value = periodSelected.value;
        reloadQuota();
      }
    }
  };

  const recordAffiliate = async (affiliate: string, plan: Plan, days: number, shopName: string | null = null, shopId: number | null = null) => {
    // Prepare payload with proper handling for API validation
    const payload: {
      affiliate: string;
      plan: Plan;
      days: number;
      shop_name: string;
      shop_id: number;
    } = {
      affiliate,
      plan,
      days,
      // Send empty string for shop_name when null (API can handle empty strings better than missing params)
      shop_name: shopName || '',
      // Send 0 for shop_id when null (0 is a valid integer that can represent "no shop")
      shop_id: shopId || 0,
    };

    await axios.post('/subscription/record-affiliate', payload);
  };

  const saveCustomer = async (data: PaddleEventData) => {
    await axios.post('/subscription/save-customer', { payload: data });
  };

  const incrementQuota = async (data: QuotaDetail): Promise<ModuleQuota> => {
    const response = await axios.post<ModuleQuota>('/quota/increment', data);

    quotas.value[data.feature] = response.data;
    return response.data;
  };

  // Every donwgrade or update redirect to success
  // except downgrade to free
  watch(currentPlan, async (plan: Plan) => {
    const MAX_RETRIES = 20;
    const RETRY_DELAY_MS = 300;

    if (plan === 'free') return;

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        const response = await axios.get<UserData>('user');
        const userPlan = window.__PLANS__.ids[response.data.plan_id];

        if (plan === userPlan) {
          break;
        }
      } catch {
        if (attempt < MAX_RETRIES - 1) {
          await promiseTimeout(RETRY_DELAY_MS);
        } else {
          window.location.search = '?timeout=1';
          return;
        }
      }
    }

    if (selectedAffiliated.value) {
      const { affiliate, days, shopName, shopId } = toValue(selectedAffiliated);
      recordAffiliate(affiliate, plan, days, shopName, shopId);
    }

    window.location.href = `${import.meta.env.VITE_APP_URL}/subscription-thanks`;
  });

  const planState = computed(() => {
    const plan = currentPlan.value;

    return {
      isFree: plan === 'free',
      isBasic: plan === 'basic',
      isPro: plan === 'pro',
      isExpert: plan === 'expert',
    };
  });

  return {
    changePlan,
    changingPlan,
    currentPlan,
    currentPlanEndDate,
    getDifferenceFromCurrentPlan,
    incrementQuota,
    isCancelling,
    isPeriodUpgrading,
    isUpgrading,
    loadingQuota,
    periodSelected,
    planLevels,
    planState,
    previewPlan,
    quotaNoneDaily,
    quotas,
    recordAffiliate,
    reloadQuota,
    reloadQuotaNoneDaily,
    saveCustomer,
    selectedAffiliated,
    selectedPlan,
    upcomingPlan,
    upcomingPlansChanges,
    userDataPeriodSelected,
  };
});
